import {
  STATUS_COPY,
  hasNoSourceData,
  sourceProvenance,
  type SourceProvenance,
} from '../provenance';
import { SOURCE_CONFIG_LABELS, SOURCE_LABELS, type AggregatedAnalyticsData, type MerchantAnalyticsConfig } from '../types';

/**
 * Per-source provenance for one report.
 *
 * The point of this panel is honesty about WHY a section of the narrative is
 * thin. A merchant reading "no notable behaviour trends" deserves to know
 * whether Google Analytics said so or was never asked. The four sources fail
 * independently and the backend reports all failures as `null`, so the verdict
 * is derived here from the raw payload plus the merchant's configured IDs.
 *
 * Note on money: Meta's `spend` is intentionally not shown. It carries no
 * currency and no unit marker, so any rendering of it would be a guess.
 */
export function SourceProvenancePanel({
  rawData,
  config,
  configLoaded,
  onConfigure,
}: {
  rawData: AggregatedAnalyticsData | null | undefined;
  config: MerchantAnalyticsConfig | undefined;
  /** False while the config query is still in flight — suppresses a wrong verdict. */
  configLoaded: boolean;
  onConfigure?: () => void;
}) {
  const provenance = sourceProvenance(rawData, configLoaded ? config : undefined);
  const nothingCollected = hasNoSourceData(provenance);

  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h2 className="text-[15px] font-semibold text-foreground">Data sources</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            What each analytics source contributed to this report.
          </p>
        </div>
        {onConfigure && (
          <button
            type="button"
            onClick={onConfigure}
            className="shrink-0 text-xs text-blue-600 hover:underline"
          >
            Configure sources
          </button>
        )}
      </div>

      {!rawData && (
        <p className="mt-3 text-[13px] text-muted-foreground">
          This version carries no source snapshot. Provenance is recorded on the original AI draft
          only — if the draft has been regenerated, earlier snapshots are not retained.
        </p>
      )}

      {nothingCollected && rawData && (
        <div className="mt-3 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          No analytics source returned data for this period. The narrative below is written from an
          empty dataset — it describes the absence of data, not the merchant&apos;s performance.
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {provenance.map((entry) => (
          <SourceCard key={entry.source} entry={entry} />
        ))}
      </div>
    </div>
  );
}

function SourceCard({ entry }: { entry: SourceProvenance }) {
  const copy = STATUS_COPY[entry.status];

  return (
    <div className="rounded-[12px] border border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-foreground">{SOURCE_LABELS[entry.source]}</span>
        <span className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${copy.tone}`}>
          {copy.label}
        </span>
      </div>

      <p className="mt-2 text-[12px] text-muted-foreground">
        {copy.detail}
        {entry.status === 'NOT_CONNECTED' && ` Set a ${SOURCE_CONFIG_LABELS[entry.source]} to enable it.`}
      </p>

      {entry.metrics.length > 0 && (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
          {entry.metrics.map((metric) => (
            <div key={metric.label} className="flex items-baseline justify-between gap-2">
              <dt className="text-[12px] text-muted-foreground truncate">{metric.label}</dt>
              <dd className="text-[12px] font-medium text-foreground tabular-nums shrink-0">
                {metric.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
